import { useEffect, useRef, useState, useCallback } from 'react';

interface UseAutoSaveOptions {
  // Retourne éventuellement une valeur (ex: indicateur de confirmation en attente),
  // ignorée par l'auto-save.
  onSave: () => Promise<unknown>;
  delay?: number; // Délai en ms avant de sauvegarder (défaut: 0ms = instantané)
  enabled?: boolean;
}

interface UseAutoSaveReturn {
  isSaving: boolean;
  lastSaved: Date | null;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  triggerSave: () => void;
}

export function useAutoSave({
  onSave,
  delay = 0,
  enabled = true,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef(false);

  // Nettoyer le timeout au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Fonction de sauvegarde
  const performSave = useCallback(async () => {
    if (!enabled || isSaving) {
      pendingSaveRef.current = true;
      return;
    }

    setIsSaving(true);
    setSaveStatus('saving');

    try {
      await onSave();
      setLastSaved(new Date());
      setSaveStatus('saved');

      // Réinitialiser le statut après 2 secondes
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);

      // Si une sauvegarde était en attente, la relancer
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        performSave();
      }
    }
  }, [enabled, isSaving, onSave]);

  // Déclencher une sauvegarde avec délai
  const triggerSave = useCallback(() => {
    if (!enabled) return;

    // Annuler le timeout précédent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Planifier la sauvegarde
    timeoutRef.current = setTimeout(() => {
      performSave();
    }, delay);
  }, [enabled, delay, performSave]);

  return {
    isSaving,
    lastSaved,
    saveStatus,
    triggerSave,
  };
}
